import React from 'react';
/**
 * PieChartRenderer — Interactive donut/pie chart with selection.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s67.
 * Optimized: extracted shared data normalization + selection hook (~211r → ~130r).
 */
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { QuestionRendererProps } from './types';
import { normalizeChartData, ChartDataPoint, CHART_COLORS } from './chartDataUtils';
import { useChartSelection } from './useChartSelection';
import { FALLBACK_COLOR, CHART_OPTION_BUTTON_CLASS } from './rendererUtils';
interface ChartEntry extends ChartDataPoint {
  name: string;
  originalIndex: number;
}
export function PieChartRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  // Sprint R-CLEANUP (F10 follow-up): pie segments keep their multi-color
  // palette (distinct colors are pedagogically necessary so kids can
  // distinguish "this slice" from "that slice"), but the SELECTION
  // emphasis — stroke + option-buttons — pulls from moduleColor so the
  // chart still belongs to its world. Closes the App-Feel §3.1 blocker
  // for pie-chart visuals.
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data || {};
  const rawData = normalizeChartData(config);
  const title = config.title || '';
  const colors = config.colors as string[] || CHART_COLORS;
  const hideValues = config.hideValues !== false;
  const chartData: ChartEntry[] = rawData.map((d, index) => ({
    ...d,
    name: d.label,
    originalIndex: index
  }));
  const {
    selectedIndex,
    handleItemClick,
    handleKeyDown
  } = useChartSelection({
    questionId: question.id,
    itemCount: chartData.length,
    idPrefix: `pie-option-${question.id}`,
    onAnswer,
    onReadyChange,
    shouldValidate
  });
  const CustomTooltip = ({
    active,
    payload
  }: {
    active?: boolean;
    payload?: Array<{
      payload: ChartEntry;
    }>;
  }) => {
    if (!active || !payload?.[0]) return null;
    const item = payload[0].payload;
    return <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-slate-200 text-sm" data-id="element-11867" data-mp="xn24cdfy7ykl">
        <span className="font-semibold text-slate-700" data-id="element-11868" data-mp="12ek2ktql7l1v">{item.name}</span>
        {!hideValues && <span className="text-slate-500 ml-2" data-id="element-11869" data-mp="17dwrdk10r273">{item.value}</span>}
      </div>;
  };
  return <div className="w-full max-w-3xl mx-auto flex flex-col items-center" data-id="element-11870" data-mp="anmclxfluek3">
      <div className="w-full h-[300px] md:h-[400px] bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-col" aria-hidden="true" data-id="element-11871" data-mp="eopueph7qkny">
        {title && <h3 className="text-center text-slate-700 font-semibold mb-2" data-id="element-11872" data-mp="jq5dwd3qsj48">
            {title}
          </h3>}
        <div className="flex-1 w-full relative" data-id="element-11873" data-mp="1v37v9ado1cpg">
          <ResponsiveContainer width="100%" height="100%" data-id="element-11874" data-mp="1v2fy3v4f76x2">
            <PieChart data-id="element-11875" data-mp="201j4biw4zj6m">
              <Pie data={chartData} cx="50%" cy="50%" innerRadius="40%" outerRadius="80%" paddingAngle={2} dataKey="value" onClick={handleItemClick} cursor="pointer" data-id="element-11876" data-mp="200r9iaad5phe">
                {chartData.map((_: ChartEntry, index: number) => {
                const isSelected = selectedIndex === index;
                return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} stroke={isSelected ? color : '#ffffff'} strokeWidth={isSelected ? 3 : 1} className="transition-all duration-200" style={{
                  opacity: selectedIndex !== null && !isSelected ? 0.6 : 1,
                  filter: isSelected ? 'drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.2))' : 'none'
                }} data-id="element-11877" data-mp="31tvfswhqxi57" data-mpi={index} />;
              })}
              </Pie>
              <Tooltip content={<CustomTooltip data-id="element-11879" data-mp="y3fkcarbi568" />} data-id="element-11878" data-mp="y3pf0bnb50i9" />
              <Legend verticalAlign="bottom" height={36} iconType="circle" formatter={value => <span className="text-slate-600 text-sm" data-id="element-11881" data-mp="o4zusc7xv3yu">{value}</span>} data-id="element-11880" data-mp="y2ea28i4lmpk" />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div role="listbox" aria-label="Kies een deel" className="flex flex-wrap justify-center gap-2 mt-4" data-id="element-11882" data-mp="f6xxo7rbsvgu">
        {chartData.map((entry: ChartEntry, index: number) => {
        const isSelected = selectedIndex === index;
        const baseClass = CHART_OPTION_BUTTON_CLASS;
        const unselectedClass = 'bg-slate-100 text-slate-700 hover:bg-slate-200 focus:ring-slate-400';
        return <button key={`option-${index}`} id={`pie-option-${question.id}-${index}`} role="option" aria-selected={isSelected} tabIndex={0} onClick={() => handleItemClick(null, index)} onKeyDown={e => handleKeyDown(e, index)} style={isSelected ? {
          backgroundColor: color,
          color: '#ffffff'
        } : undefined} className={isSelected ? `${baseClass} focus:ring-current` : `${baseClass} ${unselectedClass}`} data-id="element-11883" data-mp="3rp47gbj7wcds" data-mpi={index}>
              {entry.name}
            </button>;
      })}
      </div>

      <p className="text-center text-slate-500 text-sm mt-4" data-id="element-11884" data-mp="eip5nz2rgctn">
        Klik op een deel of gebruik de pijltjestoetsen
      </p>
    </div>;
}